import {
  COUNTRIES,
  TRIMS,
  TRIM_AVAILABILITY,
  TRIMS_UPGRADABLE,
  TRIM_UPGRADES_AVAILABLE,
  TRIM_CHANGE_AVAILABLE,
  statuses,
} from '@constants';
import moment from '@helpers/moment';
import { getActiveVehicleID } from '@utils';

export const canUpgrade = (trim, country) => {
  let upgradedAvailable = false;

  TRIM_UPGRADES_AVAILABLE[trim].forEach(currentTrim => {
    let trimName = TRIMS[currentTrim];

    if (
      TRIM_AVAILABILITY[trimName].find(countryCode => countryCode === country)
    ) {
      upgradedAvailable = true;
    }
  });

  return upgradedAvailable;
};

export const trimUpgradable = trim => TRIMS_UPGRADABLE.indexOf(trim) >= 0;

export const trimChangeAvailable = (currentTrim, otherTrim) =>
  !!TRIM_CHANGE_AVAILABLE[currentTrim].find(
    availableTrim => availableTrim === otherTrim
  );

export const getConfigurations = (viewType, collections) => {
  let configurations = [];
  let { orders, reservations, designs } = collections;
  [...orders, ...reservations].reduce(
    (acc, currentConfiguration) => {
      if (currentConfiguration.shortcode) {
        acc.configured.push(currentConfiguration);
      } else {
        acc.others.push(currentConfiguration);
      }

      return acc;
    },
    { configured: [], others: [] }
  );

  if (viewType === 'DESIGNS') {
    configurations = [...designs];
  }

  if (viewType === 'RESERVATIONS') {
    configurations = [...orders, ...reservations];
  }

  if (viewType === 'ALL') {
    configurations = { ...orders, ...reservations, ...designs };
  }

  return configurations;
};

export const getReservation = (sfResDataId, reservations) =>
  reservations.find(
    ({ reservation }) => reservation.sfResDataId === sfResDataId
  );

export const getConfiguration = (sfConfigDataId, configurations) =>
  configurations.find(
    configuration => configuration.sfConfigDataId === sfConfigDataId
  );

export const getFeatures = (metadata, trim) => {
  try {
    let features = [];

    if (!metadata.length) {
      return '';
    }

    const {
      LUCID_MODEL_VARIANT,
      LUCID_EXT_COLOR,
      LUCID_ROOF,
      LUCID_INT_THEME,
      LUCID_WHEELS,
      LUCID_DRIVETRAIN,
    } = metadata.reduce((acc, { trimType, trimName, prCode, description }) => {
      acc[trimName] = {
        name: trimName,
        type: trimType?.replace(/\\/g, '') || '',
        description: description?.replace(/\\/g, '') || '',
        code: prCode,
      };

      return acc;
    }, {});

    if (trim === TRIMS['Lucid Air Dream Edition']) {
      if (LUCID_MODEL_VARIANT.type.trim()) {
        features.push(LUCID_MODEL_VARIANT.type);
      }
    }

    if (LUCID_EXT_COLOR?.type) {
      features.push(LUCID_EXT_COLOR.type);
    }

    if (LUCID_INT_THEME?.type) {
      features.push(`${LUCID_INT_THEME.type} Interior`);
    }

    if (LUCID_WHEELS?.type) {
      features.push(`${LUCID_WHEELS.type} Wheels`);
    }

    if (LUCID_ROOF?.type) {
      features.push(`${LUCID_ROOF?.type} Roof`);
    }

    if (LUCID_DRIVETRAIN?.description) {
      features.push(LUCID_DRIVETRAIN.description);
    }

    if (!features.length) {
      return '';
    }

    const lastItem = features.splice(-1);

    return `${features.join(', ')} and ${lastItem}`;
  } catch (err) {
    return ``;
  }
};

export const buildVehicles = (payload, VehiclesData) => {
  const vehicles = [];
  const newObj = {};
  const storedAV = getActiveVehicleID();
  const newActiveVehicle = { index: 0, status: 'RESERVED' }; //default values for activeVehicle

  payload.orders.forEach(item => {
    vehicles.push({ ...item });
  });

  payload.reservations.forEach(item => {
    vehicles.push({ ...item });
  });

  newObj['vehicles'] = sortVehicles(vehicles, false);
  let hasStoredAV = false;

  if (storedAV) {
    const avIndex = newObj.vehicles.findIndex(vehicle =>
      vehicle.isOrdered
        ? vehicle.order.orderId === storedAV
        : vehicle.reservation.reservationId === storedAV
    );
    if (avIndex !== -1) {
      hasStoredAV = true;
      newActiveVehicle.index = avIndex;
      newActiveVehicle.status = ['DELIVERED', 'FULFILLED'].includes(
        newObj.vehicles[avIndex].status
      )
        ? 'DELIVERED'
        : 'RESERVED';
    }
  }
  if (!hasStoredAV && newObj.vehicles.length > 0) {
    newActiveVehicle.index = 0;
    newActiveVehicle.status = ['DELIVERED', 'FULFILLED'].includes(
      newObj.vehicles[0].status
    )
      ? 'DELIVERED'
      : 'RESERVED';
  }
  newObj['activeVehicle'] = { ...newActiveVehicle };

  if (VehiclesData) {
    // Pair infotainment vehicles.
    Object.keys(VehiclesData).forEach(vin => {
      newObj.vehicles.forEach((vehicle, index) => {
        if (vehicle.vehicleDetails?.vin === vin) {
          newObj.vehicles[index].infotainmentVehicle = VehiclesData[vin][0];
        }
      });
    });
  }

  return newObj;
};

export const sortVehicles = (vehicles, includeCancelled) => {
  const priority = {
    READY_FOR_PAYMENT: 1,
    ORDER_ELIGIBLE: 2,
    RESERVATION: 3,
    ORDER: 4,
    FULLY_PAID: 5,
    FULFILLED: 6,
    CHANGE_REQUESTED: 7,
    CANCELLATION_INITIATED: 8,
    CANCELLATION_COMPLETED: 9,
  };

  const readyForPaymentAndOrderEligbleVehicles = vehicles.filter(a =>
    ['READY_FOR_PAYMENT', 'ORDER_ELIGIBLE'].includes(a.status)
  );
  const otherVehicles = vehicles.filter(
    a =>
      ![
        'READY_FOR_PAYMENT',
        'ORDER_ELIGIBLE',
        'CANCELLATION_COMPLETED',
        'CANCELLATION_INITIATED',
      ].includes(a.status)
  );
  const cancelledVehicles = vehicles.filter(a =>
    includeCancelled
      ? ['CANCELLATION_INITIATED', 'CANCELLATION_COMPLETED'].includes(a.status)
      : ['CANCELLATION_INITIATED'].includes(a.status)
  );

  const sortedRFPAndOEVehicles = readyForPaymentAndOrderEligbleVehicles.sort(
    (a, b) => {
      if (priority[a.status] === priority[b.status]) {
        return getDate(a) > getDate(b) ? -1 : 1;
      } else {
        // order by the status priority
        return priority[a.status] < priority[b.status] ? -1 : 1;
      }
    }
  );
  const sortedOtherVehicles = otherVehicles.sort((a, b) =>
    getDate(a) > getDate(b) ? -1 : 1
  );
  // const sortedCancelledVehicles = cancelledVehicles.sort((a, b) => getDate(a) > getDate(b) ? -1 : 1);

  const sortedCancelledVehicles = cancelledVehicles.sort((a, b) => {
    if (priority[a.status] === priority[b.status]) {
      return getDate(a) > getDate(b) ? -1 : 1;
    } else {
      // order by the status priority
      return priority[a.status] < priority[b.status] ? -1 : 1;
    }
  });

  return [
    ...sortedRFPAndOEVehicles,
    ...sortedOtherVehicles,
    ...sortedCancelledVehicles,
  ];
};

export const getVehicleStatus = vehicle => {
  switch (vehicle.status) {
    case 'RESERVATION':
      return statuses.reserved;
    case 'ORDER_ELIGIBLE':
      return statuses.orderEligible;
    case 'ORDER':
      return statuses.ordered;
    case 'ORDER_PROCESSING':
      return statuses.orderProcessing;
    case 'RESERVATION_CLOSED':
      return statuses.fullyBooked;
    case 'CANCELLATION_INITIATED':
      return statuses.cancellationRequested;
    case 'CANCELLATION_COMPLETED':
      return statuses.canceled;
    case 'CHANGE_REQUESTED':
      return statuses.changeRequested;
    case 'DESIGN':
      return statuses.design;
    case 'FULLY_PAID':
      return statuses.paid;
    case 'FULFILLED':
      return statuses.fulfilled;
    case 'READY_FOR_PAYMENT':
      return statuses.readyForPayment;
    default:
      return null;
  }
};

export const getDate = (configurationData, language = 'en') => {
  try {
    let date = '';

    if (configurationData.reservation) {
      const { reservationCreated } = configurationData.reservation;
      const reservationDate = moment(reservationCreated * 1000)
        .locale(language)
        .format('DD MM YYYY');
      date = `${reservationDate}`;
    } else if (configurationData.order) {
      const orderCreated = configurationData.order?.orderCreated;
      const cancellationDate = configurationData.order?.cancellationDate;

      if (cancellationDate) {
        date = moment(cancellationDate * 1000)
          .locale(language)
          .format('DD MM YYYY');
      } else {
        date = moment(orderCreated * 1000)
          .locale(language)
          .format('DD MM YYYY');
      }
    } else {
      const { configurationCreated } = configurationData;
      date = moment(configurationCreated * 1000)
        .locale(language)
        .format('DD MM YYYY');
    }
    let parts = date.split(' ');
    date = new Date(`${parts[1]}/${parts[0]}/${parts[2]}`);
    return date;
  } catch (err) {
    return ``;
  }
};

export const getDetails = (configurationData, language) => {
  try {
    let left = '';
    let right = '';

    if (configurationData.reservation) {
      const { deposit, currencyCode, reservationCreated } =
        configurationData.reservation;
      const { symbol } = getCurrency(currencyCode);
      const reservationDate = moment(reservationCreated * 1000)
        .locale(language)
        .format('DD MMM YYYY');
      left = `${symbol}${deposit} ${currencyCode}`;
      right = `${reservationDate}`;
    } else if (configurationData.order) {
      const orderCreated = configurationData.order?.orderCreated;
      const cancellationDate = configurationData.order?.cancellationDate;

      if (cancellationDate) {
        left = moment(cancellationDate * 1000)
          .locale(language)
          .format('DD MMM YYYY');
        right = moment(cancellationDate * 1000)
          .locale(language)
          .format('LT');
      } else {
        left = moment(orderCreated * 1000)
          .locale(language)
          .format('DD MMM YYYY');
        right = moment(orderCreated * 1000)
          .locale(language)
          .format('LT');
      }
    } else {
      const { configurationCreated } = configurationData;
      left = moment(configurationCreated * 1000)
        .locale(language)
        .format('DD MMM YYYY');
      right = moment(configurationCreated * 1000)
        .locale(language)
        .format('LT');
    }

    return `${left} / ${right}`;
  } catch (err) {
    return ``;
  }
};

// TODO: cleanup
export const getCountryInfo = countryKey => COUNTRIES[countryKey];

// TODO: cleanup
export const getCurrency = currencyCode => {
  const countryKey = Object.keys(COUNTRIES).find(
    country => COUNTRIES[country]?.currency?.iso === currencyCode?.toLowerCase()
  );

  return COUNTRIES[countryKey].currency;
};

// TODO: cleanup
export const getDepositDifference = configurationData => {
  const { deposit, currencyCode, address } = configurationData.reservation;
  const { symbol, iso } = getCurrency(currencyCode);
  const countryInfo = getCountryInfo(address?.country);
  const depositDifference =
    countryInfo.currency.values['dream_edition'] - deposit;

  return {
    formattedDifference: `${symbol}${depositDifference} ${iso?.toUpperCase()}`,
    depositDifference: depositDifference,
    iso: iso,
  };
};

export const otherConfigurations = (currentReservation, configurations) => {
  const changeUnavailable = [
    'RESERVATION',
    'ORDER_ELIGIBLE',
    'ORDER_PROCESSING',
    'ORDER',
    'READY_FOR_PAYMENT',
    'FULLY_PAID',
    'FULFILLED',
    'CANCELLATION_INITIATED',
    'CANCELLATION_COMPLETED',
    'CHANGE_REQUESTED',
  ];
  const changeUnavailableSet = new Set(changeUnavailable);
  const upgradeAvailable = canUpgrade(
    currentReservation.trim,
    currentReservation?.address?.country
  );
  const otherConfigs = configurations.filter(config => {
    if (
      currentReservation.configName === config.configName ||
      changeUnavailableSet.has(config.status)
    )
      return false;

    if (
      currentReservation.trim === TRIMS['Lucid Air Dream Edition'] &&
      config.trim === TRIMS['Lucid Air Dream Edition']
    ) {
      return true;
    } else if (
      (trimUpgradable(currentReservation.trim) &&
        config.trim === TRIMS['Lucid Air Dream Edition'] &&
        upgradeAvailable) ||
      trimChangeAvailable(currentReservation.trim, config.trim)
    ) {
      return true;
    } else {
      return false;
    }
  });

  return otherConfigs;
};
